import React from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";

interface PublicNavProps {
  lastUpdated?: Date | null;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  hideLastUpdated?: boolean;
}

const PublicNav: React.FC<PublicNavProps> = () => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isArticleDetail = location.pathname.startsWith("/news/");
  const logoUrl = "https://res.cloudinary.com/djbtf9won/image/upload/v1762320179/logox_aanwli.png";

  // Get current page URL and title for sharing
  const getShareData = () => {
    const url = window.location.href;
    const title = document.title || "News Article";
    return { url, title };
  };

  const handleWhatsAppShare = () => {
    const { url, title } = getShareData();
    const text = `${title}\n\n${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank");
  };

  const instagramUrl = "https://www.instagram.com/superfastlive/";

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={1}
      sx={{
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        bgcolor: (theme) => {
          // Convert hex to RGB and blend with white (10% primary, 90% white)
          const hex = theme.palette.primary.main.replace("#", "");
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          // Blend: 10% primary + 90% white
          const blendedR = Math.round(r * 0.1 + 255 * 0.9);
          const blendedG = Math.round(g * 0.1 + 255 * 0.9);
          const blendedB = Math.round(b * 0.1 + 255 * 0.9);
          return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
        },
      }}
    >
      <Container maxWidth="lg" disableGutters sx={{ px: 1 }}>
        <Toolbar
          disableGutters
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: "56px", sm: "64px" },
            maxHeight: { xs: "56px", sm: "64px" },
            overflow: "visible",
          }}
        >
          {/* Left: Logo */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textDecoration: "none",
              flex: "0 1 auto",
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt="Logo"
              sx={{
                height: { xs: "47.04px", sm: "53.76px" },
                width: "auto",
                maxWidth: "100%",
              }}
            />
          </Box>

          {/* Center: मुख्य बातम्या and Place/Date */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {isHomePage && (
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1.44rem", sm: "1.8rem" },
                  color: "primary.main",
                  fontWeight: 660,
                  lineHeight: 1.1,
                  mb: 0,
                }}
              >
                मुख्य बातम्या
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                color: "text.primary",
                fontWeight: 500,
                mt: isHomePage ? "8px" : 0,
                lineHeight: 1,
              }}
            >
              धुळे. {(() => {
                const now = new Date();
                const marathiMonths = [
                  'जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून',
                  'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर'
                ];
                const marathiWeekdays = [
                  'रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'
                ];
                const weekday = marathiWeekdays[now.getDay()];
                const day = now.getDate();
                const month = marathiMonths[now.getMonth()];
                const year = now.getFullYear();
                return `${weekday}. ${day} ${month} ${year}`;
              })()}
            </Typography>
          </Box>

          {/* Right: Share buttons on article detail, DateTime on other pages */}
          {isArticleDetail ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flex: "0 1 auto",
                minWidth: 0,
                ml: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Share via WhatsApp">
                  <IconButton
                    onClick={handleWhatsAppShare}
                    size="small"
                    sx={{
                      color: "text.primary",
                      padding: "5px",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      component="svg"
                      sx={{
                        width: 20,
                        height: 20,
                        fill: "currentColor",
                      }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.239-.375a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </Box>
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    mt: "2px",
                    color: "text.secondary",
                  }}
                >
                  share
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Follow us on Instagram">
                  <IconButton
                    component="a"
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: "text.primary",
                      padding: "5px",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      component="svg"
                      sx={{
                        width: 20,
                        height: 20,
                        fill: "currentColor",
                      }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </Box>
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    mt: "2px",
                    color: "text.secondary",
                  }}
                >
                  Follow us
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                flex: "0 1 auto",
                minWidth: 0,
                ml: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Share via WhatsApp">
                  <IconButton
                    onClick={handleWhatsAppShare}
                    size="small"
                    sx={{
                      color: "text.primary",
                      padding: "5px",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      component="svg"
                      sx={{
                        width: 20,
                        height: 20,
                        fill: "currentColor",
                      }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.239-.375a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </Box>
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    mt: "2px",
                    color: "text.secondary",
                  }}
                >
                  share
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title="Follow us on Instagram">
                  <IconButton
                    component="a"
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: "text.primary",
                      padding: "5px",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      component="svg"
                      sx={{
                        width: 20,
                        height: 20,
                        fill: "currentColor",
                      }}
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </Box>
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    mt: "2px",
                    color: "text.secondary",
                  }}
                >
                  Follow us
                </Typography>
              </Box>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default PublicNav;
